import React, { useState, useEffect } from 'react';
import dataStore, { getProtocolSuggestions } from '../utils/dataStore';
import protocolStore from '../utils/protocolStore';
import ApplyAllCasesModal from './ApplyAllCasesModal';
import './StainProtocolMapping.css'; // Reuse the same styles

const RegionProtocolMapping = () => {
    const [dataStatus, setDataStatus] = useState(dataStore.getStatus());
    const [regionProtocols, setRegionProtocols] = useState(protocolStore.regionProtocols);
    const [availableProtocols, setAvailableProtocols] = useState([]);
    const [cases, setCases] = useState([]);
    const [currentCaseIndex, setCurrentCaseIndex] = useState(0);
    const [expandedGroups, setExpandedGroups] = useState(new Set());
    const [showApplyAllModal, setShowApplyAllModal] = useState(false);

    // Subscribe to data store changes
    useEffect(() => {
        const unsubscribe = dataStore.subscribe(() => {
            setDataStatus(dataStore.getStatus());
        });
        return unsubscribe;
    }, []);

    // Subscribe to protocol store changes
    useEffect(() => {
        const unsubscribeProtocols = protocolStore.subscribe(() => {
            setRegionProtocols(protocolStore.regionProtocols);
            setAvailableProtocols(protocolStore.regionProtocols || []);
        });

        // Initial load
        setRegionProtocols(protocolStore.regionProtocols);
        setAvailableProtocols(protocolStore.regionProtocols || []);

        return unsubscribeProtocols;
    }, []);

    // Generate cases with region slides when data changes
    useEffect(() => {
        if (dataStatus.processedData && dataStatus.processedData.length > 0) {
            console.log('🔄 RegionProtocolMapping: Regenerating cases due to data change');
            console.log('🔍 Current case ID mappings:', dataStatus.caseIdMappings);
            console.log('🔍 Number of case ID mappings:', Object.keys(dataStatus.caseIdMappings || {}).length);

            const regionCases = dataStore.generateRegionProtocolCases();
            console.log(`📊 Generated ${regionCases.length} region cases`);

            // Only reset case index if the number of cases changed significantly
            // (e.g., new cases added or cases removed), not just when data is updated
            const previousCaseCount = cases.length;
            const newCaseCount = regionCases.length;

            setCases(regionCases);

            // Only reset to first case if:
            // 1. This is the initial load (no previous cases)
            // 2. The number of cases changed significantly (more than just protocol updates)
            // 3. Current case index is out of bounds
            if (previousCaseCount === 0 ||
                Math.abs(newCaseCount - previousCaseCount) > 0 ||
                currentCaseIndex >= newCaseCount) {
                setCurrentCaseIndex(0);
            }
            // Otherwise, keep the current case index to maintain user's position
        }
    }, [dataStatus]);

    // Get all slides with region types for the case (regardless of mapping status)
    const getAllRegionSlides = (caseData) => {
        if (!caseData || !caseData.slides) return [];
        return caseData.slides.filter(slide =>
            slide.localRegionId || slide.BDSA?.bdsaLocal?.localRegionId // Check both locations
        );
    };

    // Group slides by region type
    const groupSlidesByRegionType = (slides) => {
        const groups = {};
        slides.forEach(slide => {
            // Get region type from the correct field
            const regionType = slide.localRegionId || slide.BDSA?.bdsaLocal?.localRegionId;
            if (regionType) {
                if (!groups[regionType]) {
                    groups[regionType] = [];
                }
                groups[regionType].push(slide);
            }
        });
        return groups;
    };

    // Navigation functions
    const handlePreviousCase = () => {
        const newIndex = currentCaseIndex > 0 ? currentCaseIndex - 1 : cases.length - 1;
        setCurrentCaseIndex(newIndex);
    };

    const handleNextCase = () => {
        const newIndex = currentCaseIndex < cases.length - 1 ? currentCaseIndex + 1 : 0;
        setCurrentCaseIndex(newIndex);
    };

    // Skip to next case with unmapped region protocols
    const handleSkipToNextUnmapped = () => {
        // Force a refresh of the cases data to get the latest state
        const freshCases = dataStore.generateRegionProtocolCases();
        let foundIndex = -1;

        // Search forward from current position
        for (let i = 0; i < freshCases.length; i++) {
            const caseIndex = (currentCaseIndex + i) % freshCases.length;
            const caseData = freshCases[caseIndex];

            if (caseData && caseData.slides) {
                const allRegionSlides = getAllRegionSlides(caseData);
                const regionGroups = groupSlidesByRegionType(allRegionSlides);

                // Check if any region group has unmapped slides
                const hasUnmapped = Object.entries(regionGroups).some(([regionType, slides]) => {
                    const unmappedCount = slides.filter(s => !s.bdsaRegionProtocol || s.bdsaRegionProtocol.length === 0).length;
                    return unmappedCount > 0;
                });

                if (hasUnmapped) {
                    foundIndex = caseIndex;
                    break;
                }
            }
        }

        if (foundIndex !== -1) {
            setCurrentCaseIndex(foundIndex);
            console.log(`⏭️ Skipped to case ${freshCases[foundIndex].bdsaId} with unmapped region protocols`);
        } else {
            console.log('🎉 No more cases with unmapped region protocols found!');
            alert('🎉 Great job! No more cases with unmapped region protocols found.');
        }
    };

    // Toggle group expansion
    const toggleGroupExpansion = (regionType) => {
        const newExpanded = new Set(expandedGroups);
        if (newExpanded.has(regionType)) {
            newExpanded.delete(regionType);
        } else {
            newExpanded.add(regionType);
        }
        setExpandedGroups(newExpanded);
    };

    const handleApplyRegionProtocol = (slides, protocolId, targetCase = null, batchMode = false) => {
        console.log('Applying region protocol', protocolId, 'to slides:', slides);

        // Use the provided target case, or fall back to current case
        const currentCase = targetCase || cases[currentCaseIndex];
        if (!currentCase) return;

        // Convert GUID to name for storage (data stores names, not GUIDs)
        const protocol = availableProtocols.find(p => p.id === protocolId);
        const protocolName = protocol ? protocol.name : protocolId;

        // Apply protocol to each slide using the NAME
        slides.forEach(slide => {
            dataStore.addProtocolMapping(currentCase.bdsaId, slide.id, protocolName, 'region', batchMode);
        });

        console.log(`✅ Applied protocol ${protocolName} (${protocolId}) to ${slides.length} slides`);

        // Force a re-render by updating the data status
        setDataStatus(dataStore.getStatus());
    };

    const handleRemoveRegionProtocol = (slides, protocolToRemove) => {
        console.log('🔍 Removing region protocol', protocolToRemove, 'from slides:', slides);

        // Get the current case
        const currentCase = cases[currentCaseIndex];
        if (!currentCase) {
            console.error('❌ No current case found');
            return;
        }

        // Remove protocol from each slide
        slides.forEach(slide => {
            console.log(`🔍 Removing ${protocolToRemove} from slide ${slide.id} (${slide.filename})`);
            dataStore.removeProtocolMapping(currentCase.bdsaId, slide.id, protocolToRemove, 'region');
        });

        console.log(`✅ Removed protocol ${protocolToRemove} from ${slides.length} slides`);

        // Force a re-render by updating the data status
        setDataStatus(dataStore.getStatus());
    };

    // Get suggestion for a specific region type
    const getSuggestionForRegionType = (regionType) => {
        return getProtocolSuggestions(regionType, 'region');
    };

    // Check if a protocol is suggested for a region type
    const isProtocolSuggested = (protocolName, regionType) => {
        const suggestion = getSuggestionForRegionType(regionType);
        return suggestion.suggested === protocolName;
    };

    // Get suggestion confidence for a protocol
    const getSuggestionConfidence = (protocolName, regionType) => {
        const suggestion = getSuggestionForRegionType(regionType);
        return suggestion.suggested === protocolName ? suggestion.confidence : 0;
    };

    // Helper function to get protocol name from ID
    const getProtocolName = (protocolId) => {
        const protocol = availableProtocols.find(p => p.id === protocolId);
        return protocol ? protocol.name : protocolId; // Fallback to ID if not found
    };

    // Helper function to get full protocol info for tooltips
    const getProtocolInfo = (protocolId) => {
        const protocol = availableProtocols.find(p => p.id === protocolId);
        if (protocol) {
            return {
                name: protocol.name,
                id: protocol.id,
                description: protocol.description || '',
                abbreviation: protocol.abbreviation || ''
            };
        }
        return {
            name: protocolId,
            id: protocolId,
            description: '',
            abbreviation: ''
        };
    };

    // Helper function to create tooltip text
    const getProtocolTooltip = (protocolId) => {
        const info = getProtocolInfo(protocolId);
        const parts = [];

        // Add full name
        parts.push(`Name: ${info.name}`);

        // Add GUID
        parts.push(`GUID: ${info.id}`);

        // Add abbreviation if different from name
        if (info.abbreviation && info.abbreviation !== info.name) {
            parts.push(`Abbreviation: ${info.abbreviation}`);
        }

        // Add description if available
        if (info.description) {
            parts.push(`Description: ${info.description}`);
        }

        return parts.join('\n');
    };

    // Auto-apply suggestions for the current case
    const handleAutoApplySuggestions = () => {
        const currentCase = cases[currentCaseIndex];
        if (!currentCase) return;

        const allRegionSlides = getAllRegionSlides(currentCase);
        const regionGroups = groupSlidesByRegionType(allRegionSlides);

        let appliedCount = 0;
        let skippedCount = 0;
        const skippedTypes = [];

        Object.entries(regionGroups).forEach(([regionType, slides]) => {
            const suggestion = getSuggestionForRegionType(regionType);

            // Only apply if we have high confidence (>= 80%), it's an exact match, and it's not IGNORE
            if (suggestion.suggested && suggestion.confidence >= 0.8 && suggestion.isExactMatch &&
                suggestion.suggested.toUpperCase() !== 'IGNORE') {
                // Check if this protocol is already fully applied to all slides
                const alreadyApplied = slides.every(slide =>
                    slide.bdsaRegionProtocol && slide.bdsaRegionProtocol.includes(suggestion.suggested)
                );

                if (!alreadyApplied) {
                    handleApplyRegionProtocol(slides, suggestion.suggested);
                    appliedCount++;
                }
            } else {
                skippedCount++;
                skippedTypes.push({
                    type: regionType,
                    reason: suggestion.suggested
                        ? `Low confidence (${Math.round(suggestion.confidence * 100)}%)`
                        : 'No suggestion available'
                });
            }
        });

        // Show results with better feedback - only log summary
        if (appliedCount > 0) {
            console.log(`✅ Auto-applied ${appliedCount} region protocol suggestion(s)`);
        } else if (skippedCount > 0) {
            console.log(`⚠️ No region suggestions applied (${skippedCount} skipped due to low confidence)`);
        }
    };

    // Auto-apply suggestions for ALL cases
    const handleAutoApplyAllCases = async (progressCallback) => {
        console.log(`🚀 Starting auto-apply suggestions for ${cases.length} cases`);

        let totalProcessed = 0;
        let totalApplied = 0;
        let totalSkipped = 0;

        for (let caseIndex = 0; caseIndex < cases.length; caseIndex++) {
            const caseData = cases[caseIndex];

            // Update progress
            const progress = {
                current: caseIndex + 1,
                total: cases.length,
                percentage: ((caseIndex + 1) / cases.length) * 100,
                applied: totalApplied,
                skipped: totalSkipped,
                currentCase: caseData.bdsaId
            };
            progressCallback(progress);

            // Add a small delay to make progress visible (reduced from 100ms to 20ms for better performance)
            if (cases.length > 10 && caseIndex < cases.length - 1) {
                await new Promise(resolve => setTimeout(resolve, 20));
            }

            // Use the caseData directly instead of relying on state updates
            if (caseData) {
                const allRegionSlides = getAllRegionSlides(caseData);
                const regionGroups = groupSlidesByRegionType(allRegionSlides);

                let caseApplied = 0;
                let caseSkipped = 0;

                Object.entries(regionGroups).forEach(([regionType, slides]) => {
                    const suggestion = getSuggestionForRegionType(regionType);

                    // Use the same logic as the single-case auto-apply
                    if (suggestion.suggested && suggestion.confidence >= 0.8 && suggestion.isExactMatch &&
                        suggestion.suggested.toUpperCase() !== 'IGNORE') {
                        // Check if this protocol is already fully applied to all slides
                        const alreadyApplied = slides.every(slide =>
                            slide.bdsaRegionProtocol && slide.bdsaRegionProtocol.includes(suggestion.suggested)
                        );

                        if (!alreadyApplied) {
                            handleApplyRegionProtocol(slides, suggestion.suggested, caseData, true); // Use batch mode
                            caseApplied++;
                        }
                    } else {
                        caseSkipped++;
                    }
                });

                totalApplied += caseApplied;
                totalSkipped += caseSkipped;
                totalProcessed++;
            }
        }

        // Final progress update
        const finalProgress = {
            current: cases.length,
            total: cases.length,
            percentage: 100,
            applied: totalApplied,
            skipped: totalSkipped,
            currentCase: 'Complete'
        };
        progressCallback(finalProgress);

        // Save all changes to storage and notify listeners (batch operation complete)
        dataStore.saveToStorage();
        dataStore.notify();

        console.log(`🎯 Auto-apply ALL cases complete: ${totalApplied} applied, ${totalSkipped} skipped across ${totalProcessed} cases`);

        return {
            processed: totalProcessed,
            applied: totalApplied,
            skipped: totalSkipped
        };
    };

    if (cases.length === 0) {
        return (
            <div className="stain-protocol-mapping">
                <div className="header">
                    <h2>Region Protocol Mapping</h2>
                    <p>No cases with region slides found.</p>
                </div>
                <div className="no-cases">
                    <p>Make sure you have:</p>
                    <ul>
                        <li>Loaded data with BDSA Case IDs assigned</li>
                        <li>Local Region IDs in your data</li>
                    </ul>
                </div>
            </div>
        );
    }

    const currentCase = cases[currentCaseIndex];
    const allRegionSlides = getAllRegionSlides(currentCase);
    const regionGroups = groupSlidesByRegionType(allRegionSlides);

    // Calculate unmapped slides for current case
    const getUnmappedCounts = (caseData) => {
        if (!caseData || !caseData.slides) return { stain: 0, region: 0, total: 0 };

        let unmappedStain = 0;
        let unmappedRegion = 0;

        caseData.slides.forEach(slide => {
            // Check stain protocol mapping
            if (slide.localStainID && (!slide.bdsaStainProtocol || slide.bdsaStainProtocol.length === 0)) {
                unmappedStain++;
            }
            // Check region protocol mapping  
            if (slide.localRegionId && (!slide.bdsaRegionProtocol || slide.bdsaRegionProtocol.length === 0)) {
                unmappedRegion++;
            }
        });

        return {
            stain: unmappedStain,
            region: unmappedRegion,
            total: unmappedStain + unmappedRegion
        };
    };

    const unmappedCounts = getUnmappedCounts(currentCase);

    return (
        <div className="stain-protocol-mapping">
            <div className="header">
                <div className="case-navigation-compact">
                    <button
                        className="nav-btn prev-btn"
                        onClick={handlePreviousCase}
                        disabled={cases.length <= 1}
                    >
                        ←
                    </button>
                    <div className="case-pill">
                        <div className="case-id">{currentCase.bdsaId}</div>
                        <div className="case-details">Local: {currentCase.localCaseId} • {currentCaseIndex + 1} of {cases.length}</div>
                        {unmappedCounts.total > 0 && (
                            <div className="unmapped-counts">
                                <span className="unmapped-stain">{unmappedCounts.stain} unmapped stain</span>
                                {unmappedCounts.region > 0 && (
                                    <span className="unmapped-region"> • {unmappedCounts.region} unmapped region</span>
                                )}
                            </div>
                        )}
                    </div>
                    <button
                        className="nav-btn next-btn"
                        onClick={handleNextCase}
                        disabled={cases.length <= 1}
                    >
                        →
                    </button>
                    <button
                        className="apply-all-cases-btn"
                        onClick={() => setShowApplyAllModal(true)}
                        title="Auto-apply high-confidence region protocol suggestions to all region types across ALL cases"
                    >
                        🚀 Apply to All Cases
                    </button>
                </div>
                <div className="auto-apply-section">
                    <button
                        className="auto-apply-btn"
                        onClick={handleAutoApplySuggestions}
                        title="Automatically apply high-confidence region protocol suggestions to this case"
                    >
                        ⚡ Auto-Apply Suggestions
                    </button>
                    <button
                        className="skip-unmapped-btn"
                        onClick={handleSkipToNextUnmapped}
                        title="Skip to the next case that has unmapped region protocols"
                    >
                        ⏭️ Skip to Next Unmapped
                    </button>
                </div>
            </div>

            <div className="mapping-interface">
                <h3>Map Region Protocols to Slides</h3>
                <p>Select a region protocol for each group of slides with the same region type.</p>

                {Object.keys(regionGroups).length === 0 ? (
                    <div className="no-unmapped-slides">
                        <p>No slides with region types found in this case.</p>
                    </div>
                ) : (
                    <div className="stain-groups">
                        {Object.entries(regionGroups).map(([regionType, slides]) => {
                            const isExpanded = expandedGroups.has(regionType);
                            const mappedCount = slides.filter(s => s.bdsaRegionProtocol && s.bdsaRegionProtocol.length > 0).length;
                            const unmappedCount = slides.filter(s => !s.bdsaRegionProtocol || s.bdsaRegionProtocol.length === 0).length;

                            // Find all protocols that are applied to any slide in this group with counts
                            const protocolCounts = {};
                            slides.forEach(slide => {
                                (slide.bdsaRegionProtocol || []).forEach(protocol => {
                                    protocolCounts[protocol] = (protocolCounts[protocol] || 0) + 1;
                                });
                            });

                            const allGroupProtocols = Object.keys(protocolCounts);
                            const fullyAppliedProtocols = allGroupProtocols.filter(protocol =>
                                protocolCounts[protocol] === slides.length
                            );

                            // Helper function to convert protocol names to GUIDs for comparison
                            const getProtocolGuid = (protocolName) => {
                                // Try exact match first
                                let protocol = availableProtocols.find(p => p.name === protocolName);

                                // If no exact match, try case-insensitive match
                                if (!protocol) {
                                    protocol = availableProtocols.find(p =>
                                        p.name.toLowerCase() === protocolName.toLowerCase()
                                    );
                                }

                                // If still no match, try partial match (protocol name might be truncated)
                                if (!protocol) {
                                    protocol = availableProtocols.find(p =>
                                        p.name.toLowerCase().startsWith(protocolName.toLowerCase()) ||
                                        protocolName.toLowerCase().startsWith(p.name.toLowerCase())
                                    );
                                }

                                return protocol ? protocol.id : protocolName;
                            };

                            // Convert fully applied protocol names to GUIDs for filtering
                            const fullyAppliedGuids = fullyAppliedProtocols.map(getProtocolGuid);
                            const allGroupGuids = allGroupProtocols.map(getProtocolGuid);


                            return (
                                <div key={regionType} className="stain-group">
                                    <div
                                        className="group-header clickable"
                                        onClick={() => toggleGroupExpansion(regionType)}
                                    >
                                        <div className="group-title">
                                            <span className="expand-icon">{isExpanded ? '▼' : '▶'}</span>
                                            <h4>Region Type: {regionType}</h4>
                                        </div>
                                        <div className="group-stats">
                                            <p>{slides.length} slide(s)</p>
                                            {mappedCount > 0 && <span className="mapped-count">({mappedCount} mapped)</span>}
                                            {unmappedCount > 0 && <span className="unmapped-count">({unmappedCount} unmapped)</span>}
                                        </div>
                                    </div>

                                    {/* Group-level protocols with counts */}
                                    {allGroupProtocols.length > 0 && (
                                        <div className="group-protocols">
                                            <strong>Group Protocols:</strong>
                                            <div className="protocol-chips">
                                                {allGroupProtocols.map((protocol, idx) => {
                                                    const count = protocolCounts[protocol];
                                                    const isFullyApplied = count === slides.length;
                                                    return (
                                                        <span key={idx} className={`protocol-chip group-chip ${isFullyApplied ? 'fully-applied' : 'partially-applied'}`}>
                                                            <span title={getProtocolTooltip(protocol)}>{getProtocolName(protocol)} ({count}/{slides.length})</span>
                                                            <button
                                                                className="remove-protocol-btn"
                                                                onClick={() => handleRemoveRegionProtocol(slides, protocol)}
                                                                title={`Remove ${getProtocolName(protocol)} from all slides`}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Protocol selection - always visible */}
                                    <div className="protocol-selection">
                                        <label>Add Region Protocol:</label>
                                        <div className="available-protocols">
                                            {availableProtocols
                                                .filter(protocol => !allGroupGuids.includes(protocol.id))
                                                .map(protocol => {
                                                    const isPartiallyApplied = allGroupGuids.includes(protocol.id);
                                                    const currentCount = protocolCounts[protocol.name] || 0;
                                                    const isSuggested = isProtocolSuggested(protocol.name, regionType);
                                                    const confidence = getSuggestionConfidence(protocol.name, regionType);

                                                    return (
                                                        <button
                                                            key={protocol.id}
                                                            className={`add-protocol-btn ${isPartiallyApplied ? 'partially-applied' : ''} ${isSuggested ? 'suggested' : ''}`}
                                                            onClick={() => handleApplyRegionProtocol(slides, protocol.id)}
                                                            title={isSuggested
                                                                ? `⭐ SUGGESTED: ${protocol.name} (${Math.round(confidence * 100)}% confidence) - ${getSuggestionForRegionType(regionType).reason}`
                                                                : isPartiallyApplied
                                                                    ? `Apply ${protocol.name} to remaining ${slides.length - currentCount} slides`
                                                                    : `Add ${protocol.name} to all slides`
                                                            }
                                                        >
                                                            {isSuggested && '⭐ '}
                                                            + {protocol.name}
                                                            {isPartiallyApplied && ` (${currentCount}/${slides.length})`}
                                                        </button>
                                                    );
                                                })}
                                        </div>
                                    </div>

                                    {isExpanded && (
                                        <div className="slides-table-container">
                                            <table className="slides-table">
                                                <thead>
                                                    <tr>
                                                        <th>File</th>
                                                        <th>Status</th>
                                                        <th>Protocols</th>
                                                        <th>Actions</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {slides.map(slide => (
                                                        <tr key={slide.id} className={`slide-row ${(slide.bdsaRegionProtocol && slide.bdsaRegionProtocol.length > 0) ? 'mapped' : 'unmapped'}`}>
                                                            <td className="file-cell">
                                                                <div className="file-name">{slide.filename}</div>
                                                                <div className="slide-id">{slide.id}</div>
                                                            </td>
                                                            <td className="status-cell">
                                                                <span className={`status-badge ${(slide.bdsaRegionProtocol && slide.bdsaRegionProtocol.length > 0) ? 'mapped' : 'unmapped'}`}>
                                                                    {(slide.bdsaRegionProtocol && slide.bdsaRegionProtocol.length > 0) ? '✓ Mapped' : '○ Unmapped'}
                                                                </span>
                                                            </td>
                                                            <td className="protocols-cell">
                                                                {slide.bdsaRegionProtocol && slide.bdsaRegionProtocol.length > 0 ? (
                                                                    <div className="protocol-tags">
                                                                        {slide.bdsaRegionProtocol.map((protocol, idx) => (
                                                                            <span key={idx} className="protocol-tag" title={getProtocolTooltip(protocol)}>
                                                                                {getProtocolName(protocol)}
                                                                                <button
                                                                                    className="remove-protocol-btn"
                                                                                    onClick={() => handleRemoveRegionProtocol([slide], protocol)}
                                                                                    title={`Remove ${getProtocolName(protocol)}`}
                                                                                >
                                                                                    ×
                                                                                </button>
                                                                            </span>
                                                                        ))}
                                                                    </div>
                                                                ) : (
                                                                    <span className="no-protocols">No protocols</span>
                                                                )}
                                                            </td>
                                                            <td className="actions-cell">
                                                                <div className="slide-actions">
                                                                    {availableProtocols
                                                                        .filter(protocol => !(slide.bdsaRegionProtocol || []).includes(protocol.id))
                                                                        .map(protocol => (
                                                                            <button
                                                                                key={protocol.id}
                                                                                className="apply-protocol-btn"
                                                                                onClick={() => handleApplyRegionProtocol([slide], protocol.id)}
                                                                                title={`Apply ${protocol.name}`}
                                                                            >
                                                                                + {protocol.name}
                                                                            </button>
                                                                        ))}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <ApplyAllCasesModal
                isOpen={showApplyAllModal}
                onClose={() => setShowApplyAllModal(false)}
                onApplyAll={handleAutoApplyAllCases}
                protocolType="region"
                totalCases={cases.length}
            />
        </div>
    );
};

export default RegionProtocolMapping;
